import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';
import { 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  Hammer, 
  Compass, 
  Trophy, 
  BookOpen,
  Check,
  ChevronRight,
  Sun,
  Heart
} from 'lucide-react';

// Camp-specific images - Diverse children in premium settings reflecting Kenya
const IMAGES = {
  // Hero: Two diverse young girls (one black, one white) by a tent - camp setting
  hero: "https://images.unsplash.com/photo-1674230316788-d9c8b92f0d63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwYmxhY2slMjBjaGlsZHJlbiUyMG91dGRvb3IlMjBjYW1wJTIwcGxheWluZ3xlbnwwfHx8fDE3Nzc5MTc2MDh8MA&ixlib=rb-4.1.0&q=85",
  // Build & Create: Children doing arts and crafts at table
  build: "https://images.pexels.com/photos/6941676/pexels-photo-6941676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  // Explore & Discover: Black child in red jacket exploring misty forest path
  explore: "https://images.unsplash.com/photo-1770034511662-f8feea5124c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxibGFjayUyMGtpZHMlMjBleHBsb3JpbmclMjBvdXRkb29ycyUyMG5hdHVyZSUyMGRpc2NvdmVyeXxlbnwwfHx8fDE3Nzc5MTc4NTF8MA&ixlib=rb-4.1.0&q=85",
  // Games & Team Challenges: Kids playing football/soccer - diverse, modern
  games: "https://images.pexels.com/photos/28260833/pexels-photo-28260833.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  // Reflection & Storytelling: Young Black girl reading book in modern library
  storytelling: "https://images.pexels.com/photos/6550461/pexels-photo-6550461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  // Animals/About section: Diverse group of young people sitting together
  animals: "https://images.unsplash.com/photo-1701834951900-b31c99da66f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxraWRzJTIwZ3JvdXAlMjBhY3Rpdml0eSUyMG91dGRvb3IlMjBmdW4lMjBkaXZlcnNlfGVufDB8fHx8MTc3NzkxNzAzMHww&ixlib=rb-4.1.0&q=85",
  // Running: Girl running while laughing
  running: "https://images.unsplash.com/photo-1533222481259-ce20eda1e20b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMG91dGRvb3IlMjBsZWFybmluZyUyMHByZW1pdW0lMjBzdW1tZXIlMjBjYW1wJTIwYWN0aXZpdGllc3xlbnwwfHx8fDE3Nzc5MTcwMDZ8MA&ixlib=rb-4.1.0&q=85",
};

const quickDetails = [
  { icon: Calendar, label: '6th July – 31st July' },
  { icon: Clock, label: '8:00am – 3:00pm' },
  { icon: Users, label: 'Ages 1–11' },
  { icon: Heart, label: 'Maximum 32 children' },
  { icon: Sun, label: 'Outdoor learning + animals + creativity' },
  { icon: MapPin, label: 'Ridgeways / Stables location' },
];

const pricingOptions = [
  {
    title: 'Daily Pass',
    price: 'KES 3,000',
    period: 'per day',
    description: 'Limited availability. Best for flexible attendance.',
    features: ['Single day access', 'All camp activities included', 'Snacks not included'],
    highlight: false,
  },
  {
    title: 'Weekly Experience',
    price: 'KES 13,500',
    period: 'per week',
    description: 'Weeks run Monday to Friday.',
    features: [
      'Week 1: 6–10 July',
      'Week 2: 13–17 July',
      'Week 3: 20–24 July',
      'Week 4: 27–31 July',
    ],
    highlight: false,
  },
  {
    title: 'Full Camp Experience',
    price: 'KES 50,000',
    period: 'full 4-week programme',
    description: 'Best value and priority placement.',
    features: ['All 4 weeks included', 'Priority placement', 'Best value option'],
    highlight: true,
  },
];

const activityCards = [
  {
    icon: Hammer,
    title: 'Build & Create',
    description: 'Children make, design and build with their hands.',
    activities: [
      'Den building using wood, tarps and rope',
      'Mini shelter challenges',
      'Simple woodworking',
      'Lego / Young Engineers builds',
      'Jewellery making',
      'Cardboard engineering',
      'Bridge-building challenges',
    ],
    image: IMAGES.build,
  },
  {
    icon: Compass,
    title: 'Explore & Discover',
    description: 'Children investigate nature, science and the world around them.',
    activities: [
      'Science experiments',
      'Nature walks',
      'Bug hunting and observation',
      'Animal care with chickens and bunnies',
      'Planting and gardening',
      'Water flow and dam-building experiments',
      'Paper plane and parachute testing',
    ],
    image: IMAGES.explore,
  },
  {
    icon: Trophy,
    title: 'Games & Team Challenges',
    description: 'Children build confidence, resilience and teamwork.',
    activities: [
      'Obstacle courses',
      'Relay races',
      'Capture the flag',
      'Treasure hunts',
      'Tug of war',
      'Mini Olympics',
      'Problem-solving team missions',
    ],
    image: IMAGES.games,
  },
  {
    icon: BookOpen,
    title: 'Reflection & Storytelling',
    description: 'Children develop voice, confidence and self-awareness.',
    activities: [
      'End-of-day sharing circles',
      'Drawing and journaling',
      'Storytelling',
      'Show-and-tell',
      'Mini performances',
      'Group reflection questions',
    ],
    image: IMAGES.storytelling,
  },
];

const weeklyThemes = [
  {
    week: 'Week 1',
    title: 'Build & Belong',
    dates: '6–10 July',
    description: 'Children settle in, form friendships and take on hands-on building challenges.',
  },
  {
    week: 'Week 2',
    title: 'Invent & Create',
    dates: '13–17 July',
    description: 'A week of creativity, design, engineering, Lego, jewellery making and maker challenges.',
  },
  {
    week: 'Week 3',
    title: 'Explore & Discover',
    dates: '20–24 July',
    description: 'Science, nature and animal care come to life through experiments and outdoor exploration.',
  },
  {
    week: 'Week 4',
    title: 'Lead & Perform',
    dates: '27–31 July',
    description: 'Children build confidence through teamwork, storytelling, competitions and a final showcase.',
  },
];

const dailySchedule = [
  { time: '8:00 – 9:00', title: 'Arrival & Free Exploration', description: 'Children settle in through outdoor play, drawing, reading, building and nature exploration.' },
  { time: '9:00 – 9:30', title: 'Morning Circle', description: 'Welcome, intention setting, storytelling and group connection.' },
  { time: '9:30 – 10:45', title: 'Build & Create', description: 'Hands-on projects such as den building, crafts, design and engineering challenges.' },
  { time: '10:45 – 11:15', title: 'Snack & Free Play', description: '' },
  { time: '11:15 – 12:30', title: 'Explore & Discover', description: 'Science experiments, nature exploration, animal care and problem-solving.' },
  { time: '12:30 – 1:15', title: 'Lunch', description: 'Children bring packed lunch, snacks and water bottle.' },
  { time: '1:15 – 2:15', title: 'Games & Team Challenges', description: 'Group games, sports, obstacle courses, competitions and leadership challenges.' },
  { time: '2:15 – 2:45', title: 'Reflection & Storytelling', description: 'Children share, journal, draw, present ideas or take part in storytelling.' },
  { time: '2:45 – 3:00', title: 'Wind Down & Pick Up', description: '' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const whatToBring = [
  'Packed lunch',
  'Snacks',
  'Water bottle',
  'Wellington boots, Crocs or outdoor shoes',
  'Sun hat',
  'Change of clothes',
  'Jumper or light rain jacket',
];

export default function SummerCamp() {
  const [formData, setFormData] = useState({
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    childName: '',
    childAge: '',
    preferredOption: '',
    preferredDates: '',
    allergies: '',
    additionalNotes: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/enquiry/camp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_name: formData.parentName,
          parent_phone: formData.parentPhone,
          parent_email: formData.parentEmail,
          child_name: formData.childName,
          child_age: formData.childAge,
          preferred_option: formData.preferredOption,
          preferred_dates: formData.preferredDates,
          allergies: formData.allergies,
          additional_notes: formData.additionalNotes,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at info@novaroots.ke');
      console.error('Camp enquiry submission error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="summer-camp-page" className="page-transition">
      {/* Hero Section */}
      <section data-testid="camp-hero-section" className="relative h-screen min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 animate-slow-zoom origin-center">
            <img
              src={IMAGES.hero}
              alt="Children enjoying outdoor camp activities"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D4A3E]/85 via-[#2D4A3E]/70 to-[#2D4A3E]/50" />
        </div>
        
        <div className="relative z-10 container-nova text-white pt-24">
          <ScrollReveal>
            <div className="inline-block bg-[#C27856] text-white text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-full mb-6">
              Limited to 32 Children
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] max-w-3xl">
              Nova Roots Summer Camp
            </h1>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <p className="mt-8 text-xl md:text-2xl font-light leading-relaxed max-w-xl text-white/95">
              A hands-on outdoor learning camp where children build, create, explore, care for animals and grow in confidence.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <p className="mt-6 text-base font-light leading-relaxed max-w-lg text-white/80">
              July 6th – 31st. This is not a holiday club. It's a living preview of the Nova Roots learning village.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={400}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a 
                href="#enquiry-form" 
                className="bg-[#C27856] text-white hover:bg-[#A8654A] px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300 text-center shadow-lg"
                data-testid="camp-hero-cta-primary"
              >
                Reserve Your Spot
              </a>
              <a 
                href="#camp-details" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#1A3C34] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300 text-center"
                data-testid="camp-hero-cta-secondary"
              >
                View Pricing
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Quick Details Section */}
      <section id="camp-details" className="py-16 md:py-20 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {quickDetails.map((detail, index) => (
                <div 
                  key={index}
                  className="bg-white border border-[#EBE8E0] rounded-xl p-5 text-center hover:border-[#C27856]/40 transition-colors duration-300"
                  data-testid={`quick-detail-${index}`}
                >
                  <detail.icon className="w-6 h-6 mx-auto text-[#C27856] mb-3" />
                  <p className="text-sm font-light text-[#1A3C34]">{detail.label}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <ScrollReveal>
              <div className="img-hover-zoom rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.animals}
                  alt="Children caring for farm animals"
                  className="w-full h-[350px] md:h-[450px] object-cover"
                />
              </div>
            </ScrollReveal>
            
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] leading-[1.2]">
                  A Living Preview of the Nova Roots Learning Village
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A]">
                  Children will spend their days building dens, creating projects, experimenting, storytelling, playing outdoors, caring for animals and developing confidence through teamwork and real-world learning.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={250}>
                <p className="mt-6 text-base font-light leading-relaxed text-[#5C6B6B]">
                  This is not a basic holiday club. It is a living preview of the Nova Roots learning village.
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                Choose Your Camp Experience
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pricingOptions.map((option, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div 
                  className={`relative h-full bg-white border rounded-2xl p-8 transition-all duration-300 ${
                    option.highlight 
                      ? 'border-[#C27856] shadow-lg' 
                      : 'border-[#EBE8E0] hover:border-[#C27856]/40'
                  }`}
                  data-testid={`pricing-card-${index}`}
                >
                  {option.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C27856] text-white text-xs font-medium px-4 py-1 rounded-full">
                      Best Value
                    </div>
                  )}
                  <h3 className="font-heading text-xl font-medium text-[#1A3C34]">{option.title}</h3>
                  <div className="mt-4">
                    <span className="font-heading text-3xl font-light text-[#1A3C34]">{option.price}</span>
                    <span className="text-sm text-[#5C6B6B] ml-2">{option.period}</span>
                  </div>
                  <p className="mt-4 text-sm font-light text-[#5C6B6B]">{option.description}</p>
                  <ul className="mt-6 space-y-3">
                    {option.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3 text-sm text-[#2D3A3A]">
                        <Check className="w-4 h-4 text-[#C27856] mt-0.5 flex-shrink-0" />
                        <span className="font-light">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
          
          {/* Sibling Benefit */}
          <ScrollReveal delay={400}>
            <div className="mt-12 bg-[#1A3C34] rounded-2xl p-8 text-center">
              <h3 className="font-heading text-xl font-medium text-[#F9F8F4]">Sibling Benefit</h3>
              <div className="mt-4 flex flex-wrap justify-center gap-8 text-[#F9F8F4]/90">
                <div>
                  <p className="text-sm text-[#C27856]">First child</p>
                  <p className="font-heading text-lg">KES 50,000</p>
                </div>
                <div>
                  <p className="text-sm text-[#C27856]">Second child</p>
                  <p className="font-heading text-lg">KES 45,000</p>
                </div>
                <div>
                  <p className="text-sm text-[#C27856]">Additional siblings</p>
                  <p className="font-heading text-lg">Discussed directly</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* What Children Will Do */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                What Children Will Do
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-8">
            {activityCards.map((card, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div 
                  className="bg-white border border-[#EBE8E0] rounded-2xl overflow-hidden hover:border-[#C27856]/40 transition-all duration-300"
                  data-testid={`activity-card-${index}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={card.image} 
                      alt={card.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#C27856]/10 flex items-center justify-center">
                        <card.icon className="w-5 h-5 text-[#C27856]" />
                      </div>
                      <h3 className="font-heading text-xl font-medium text-[#1A3C34]">{card.title}</h3>
                    </div>
                    <p className="text-sm font-light text-[#5C6B6B] mb-6">{card.description}</p>
                    <ul className="space-y-2">
                      {card.activities.slice(0, 5).map((activity, aIndex) => (
                        <li key={aIndex} className="flex items-start gap-2 text-sm text-[#2D3A3A]">
                          <ChevronRight className="w-4 h-4 text-[#C27856] mt-0.5 flex-shrink-0" />
                          <span className="font-light">{activity}</span>
                        </li>
                      ))}
                      {card.activities.length > 5 && (
                        <li className="text-sm text-[#5C6B6B] font-light pl-6">
                          + {card.activities.length - 5} more activities
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Themes */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                Weekly Themes
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {weeklyThemes.map((theme, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div 
                  className="bg-white border border-[#EBE8E0] rounded-2xl p-6 hover:border-[#C27856]/40 transition-all duration-300 h-full"
                  data-testid={`weekly-theme-${index}`}
                >
                  <p className="text-xs font-medium uppercase tracking-wider text-[#C27856] mb-2">{theme.week}</p>
                  <h3 className="font-heading text-lg font-medium text-[#1A3C34]">{theme.title}</h3>
                  <p className="text-sm text-[#5C6B6B] mt-1 mb-4">{theme.dates}</p>
                  <p className="text-sm font-light text-[#2D3A3A]">{theme.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* A Day at Camp */}
      <section className="py-20 md:py-28 bg-[#1A3C34]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#F9F8F4]">
                A Day at Camp
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="max-w-3xl mx-auto">
            {dailySchedule.map((item, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <div 
                  className={`flex gap-6 pb-8 ${index < dailySchedule.length - 1 ? 'border-b border-[#F9F8F4]/20' : ''} ${index > 0 ? 'pt-8' : ''}`}
                  data-testid={`schedule-item-${index}`}
                >
                  <div className="w-28 flex-shrink-0">
                    <p className="text-sm font-medium text-[#C27856]">{item.time}</p>
                  </div>
                  <div>
                    <h4 className="font-heading text-base font-medium text-[#F9F8F4]">{item.title}</h4>
                    {item.description && (
                      <p className="mt-2 text-sm font-light text-[#F9F8F4]/70">{item.description}</p>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] leading-[1.2]">
                  What to Bring
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <p className="mt-6 text-base font-light text-[#5C6B6B]">Children should bring:</p>
                <ul className="mt-6 space-y-3">
                  {whatToBring.map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-[#2D3A3A]">
                      <Check className="w-5 h-5 text-[#C27856] flex-shrink-0" />
                      <span className="font-light">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
              
              <ScrollReveal delay={250}>
                <div className="mt-8 bg-[#1A3C34] rounded-xl p-6">
                  <p className="text-sm text-[#C27856] mb-2">Provided by Nova Roots</p>
                  <p className="text-[#F9F8F4] font-light">Waterproof overalls</p>
                </div>
              </ScrollReveal>
            </div>
            
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] leading-[1.2]">
                  Safety & Care
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <div className="mt-6 space-y-4 text-base font-light text-[#2D3A3A]">
                  <p>
                    Nova Roots Summer Camp is small by design and capped at 32 children.
                  </p>
                  <p>
                    Children will be supervised by a dedicated camp team. The camp area will be clearly designated and children will not have unsupervised access to the stables or animals.
                  </p>
                  <p>
                    Parents must disclose allergies, medical needs or additional support needs during booking.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Booking & Payment */}
      <section className="py-20 md:py-28 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                Booking & Payment
              </h2>
              <p className="mt-4 text-base font-light text-[#5C6B6B]">
                Spaces are limited and secured by deposit.
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <ScrollReveal>
              <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6 text-center">
                <h3 className="font-heading text-lg font-medium text-[#1A3C34]">Full Camp</h3>
                <p className="font-heading text-2xl text-[#1A3C34] mt-2">KES 50,000</p>
                <div className="mt-4 text-sm text-[#5C6B6B] space-y-1">
                  <p>Deposit: KES 15,000</p>
                  <p>Balance due before 1st July</p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6 text-center">
                <h3 className="font-heading text-lg font-medium text-[#1A3C34]">Weekly Booking</h3>
                <p className="font-heading text-2xl text-[#1A3C34] mt-2">KES 13,500</p>
                <div className="mt-4 text-sm text-[#5C6B6B] space-y-1">
                  <p>Deposit: KES 5,000</p>
                  <p>Balance due before selected week</p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6 text-center">
                <h3 className="font-heading text-lg font-medium text-[#1A3C34]">Daily Pass</h3>
                <p className="font-heading text-2xl text-[#1A3C34] mt-2">KES 3,000</p>
                <div className="mt-4 text-sm text-[#5C6B6B] space-y-1">
                  <p>Full payment at booking</p>
                  <p>&nbsp;</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
          
          <ScrollReveal delay={300}>
            <p className="mt-8 text-center text-sm text-[#5C6B6B]">
              Deposits are non-refundable. Transfers may be considered subject to availability.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" className="py-20 md:py-28 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-12">
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  Camp Enquiry
                </h2>
                <p className="mt-4 text-base font-light text-[#5C6B6B]">
                  Register your interest in Nova Roots Summer Camp.
                </p>
              </div>
            </ScrollReveal>
            
            {isSubmitted ? (
              <ScrollReveal>
                <div 
                  className="bg-[#1A3C34] text-[#F9F8F4] rounded-2xl p-12 text-center"
                  data-testid="camp-enquiry-success"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C27856]/20 flex items-center justify-center">
                    <Check className="w-8 h-8 text-[#C27856]" />
                  </div>
                  <h3 className="font-heading text-2xl font-light">Thank You</h3>
                  <p className="mt-4 font-light text-[#F9F8F4]/80">
                    We have received your enquiry for Nova Roots Summer Camp. Our team will be in touch shortly to discuss next steps.
                  </p>
                  <Link 
                    to="/"
                    className="mt-8 inline-block text-[#C27856] underline underline-offset-4 hover:text-[#D4A373] transition-colors"
                  >
                    Return to Home
                  </Link>
                </div>
              </ScrollReveal>
            ) : (
              <ScrollReveal>
                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  data-testid="camp-enquiry-form"
                >
                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                        Parent Name *
                      </label>
                      <input
                        type="text"
                        name="parentName"
                        required
                        value={formData.parentName}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                        data-testid="camp-input-parent-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                        Parent Phone *
                      </label>
                      <input
                        type="tel"
                        name="parentPhone"
                        required
                        value={formData.parentPhone}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                        data-testid="camp-input-parent-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                      Parent Email *
                    </label>
                    <input
                      type="email"
                      name="parentEmail"
                      required
                      value={formData.parentEmail}
                      onChange={handleChange}
                      className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                      data-testid="camp-input-parent-email"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                        Child Name *
                      </label>
                      <input
                        type="text"
                        name="childName"
                        required
                        value={formData.childName}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                        data-testid="camp-input-child-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                        Child Age *
                      </label>
                      <input
                        type="number"
                        name="childAge"
                        required
                        min="4"
                        max="10"
                        value={formData.childAge}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                        data-testid="camp-input-child-age"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                      Preferred Option *
                    </label>
                    <select
                      name="preferredOption"
                      required
                      value={formData.preferredOption}
                      onChange={handleChange}
                      className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] focus:outline-none focus:border-[#C27856] transition-colors"
                      data-testid="camp-select-option"
                    >
                      <option value="">Select an option</option>
                      <option value="daily">Daily Pass - KES 3,000/day</option>
                      <option value="weekly">Weekly Experience - KES 13,500/week</option>
                      <option value="full">Full Camp - KES 50,000</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                      Preferred Dates or Weeks
                    </label>
                    <input
                      type="text"
                      name="preferredDates"
                      value={formData.preferredDates}
                      onChange={handleChange}
                      placeholder="e.g., Week 1 & 2, or specific dates"
                      className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors"
                      data-testid="camp-input-dates"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                      Allergies or Medical Needs
                    </label>
                    <textarea
                      name="allergies"
                      rows="3"
                      value={formData.allergies}
                      onChange={handleChange}
                      className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors resize-none"
                      data-testid="camp-input-allergies"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3C34] mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="additionalNotes"
                      rows="3"
                      value={formData.additionalNotes}
                      onChange={handleChange}
                      className="w-full bg-white border border-[#EBE8E0] rounded-lg px-4 py-3 text-[#1A3C34] placeholder:text-[#1A3C34]/40 focus:outline-none focus:border-[#C27856] transition-colors resize-none"
                      data-testid="camp-input-notes"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#C27856] text-white hover:bg-[#A8654A] px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="camp-submit-btn"
                  >
                    {isLoading ? 'Submitting...' : 'Submit Enquiry'}
                  </button>
                </form>
              </ScrollReveal>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
              6th July – 31st July 2026
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              32 Places. 4 Weeks. One Summer to Remember.
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/80 max-w-md mx-auto">
              Join us for a hands-on outdoor learning experience at the Nova Roots Summer Camp.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <a 
              href="#enquiry-form" 
              className="mt-10 inline-block bg-[#C27856] text-white hover:bg-[#A8654A] px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="camp-final-cta"
            >
              Reserve Your Spot
            </a>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
